/**
 * buildChatCallbacks — the `useChat({ … })` event-callback block extracted from
 * ChatBox. The inline object literal recreated a fresh set of callbacks on every
 * render (closing over the latest ChatBox state). This builder preserves those
 * exact semantics: ChatBox calls it once per render with a fresh `deps` object
 * and spreads the returned callbacks into `useChat`, so each callback always
 * reads the latest values.
 *
 * Only the callback fields move here; the static `useChat` inputs (sessionId,
 * mode, model, active-module/context fields) stay inline in ChatBox so the call
 * site still reads like a configuration block. Refs (voiceModeRef,
 * toolInvalidationTimerRef, postStreamEnrichmentTimersRef) are threaded by
 * reference so reads/writes stay shared with ChatBox.
 */
import type { MutableRefObject } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/keys';
import { splitResultCards } from '@/features/chat/components/result-cards/types';
import type { ChatUIAction } from '@/lib/api/endpoints/chat';
import type { ChatMessage } from '@/features/chat/hooks/useChat';
import type { Conversation } from '@/types/conversations';

export interface ChatCallbacks {
  onQuickAck: (text: string) => void;
  onMessage: (message: ChatMessage) => void;
  onStreamTextChunk: (text: string) => void;
  onToolActivity: () => void;
  onObjectCreated: (() => void) | ((...args: any[]) => void);
  onUiActions: (actions: unknown) => void;
  onStreamEnd: () => void;
}

export interface BuildChatCallbacksDeps {
  activeConversation: Conversation | null;
  workspaceId: string;
  queryClient: QueryClient;
  voiceModeRef: MutableRefObject<any>;
  toolInvalidationTimerRef: MutableRefObject<ReturnType<typeof setTimeout> | null>;
  postStreamEnrichmentTimersRef: MutableRefObject<Array<ReturnType<typeof setTimeout>>>;
  invalidateConversationObjects: (conversationId?: string) => void;
  invalidateConversationStructure: (conversationId: string) => void;
  scheduleAutoTitleFollowupRefresh: (
    conversationId: string,
    conversationName: string | null | undefined,
  ) => void;
  markCreated: (...args: any[]) => void;
  applyUiActions: (actions: ChatUIAction[]) => void;
}

export function buildChatCallbacks(deps: BuildChatCallbacksDeps): ChatCallbacks {
  const {
    activeConversation,
    workspaceId,
    queryClient,
    voiceModeRef,
    toolInvalidationTimerRef,
    postStreamEnrichmentTimersRef,
    invalidateConversationObjects,
    invalidateConversationStructure,
    scheduleAutoTitleFollowupRefresh,
    markCreated,
    applyUiActions,
  } = deps;

  return {
    onQuickAck: (text) => {
      // Quick-ack not used for voice TTS (single-speech model).
      // Still fires for non-voice text chat streaming indicators.
      if (text) console.log("[ChatBox] Quick-ack (text only):", text);
    },
    onMessage: (message) => {
      if (activeConversation?.id) {
        invalidateConversationObjects(activeConversation.id);
      }
      const vm = voiceModeRef.current;
      if (vm?.isActive && message.role === 'assistant' && message.content) {
        if (vm.streamingTtsFailed) {
          // Streaming TTS failed — fall back to full-text single-shot TTS.
          // Strip any result-card marker so the JSON payload isn't read aloud.
          console.log("[ChatBox] Streaming TTS failed, falling back to speakResponse");
          vm.speakResponse(splitResultCards(message.content).cleanContent);
        }
        // Otherwise: no-op — streaming TTS already handled via onStreamTextChunk
      }
    },
    onStreamTextChunk: (text) => {
      try {
        const vm = voiceModeRef.current;
        if (vm?.isActive) {
          vm.feedTextChunk(text);
        }
      } catch (err) {
        console.error("[ChatBox] feedTextChunk error:", err);
      }
    },
    onToolActivity: () => {
      // Agent is using tools (creating/updating objects) — refresh data.
      // Workspace MODULES refresh IMMEDIATELY (not behind the objects debounce): a
      // chat-driven module-config change (a set_label_color recolor, add_kanban_column,
      // report config, …) must repaint the OPEN pane instantly. invalidateQueries
      // refetches the active modules-list observer — ChatBox's paneModulesQuery, which
      // overlays the fresh config onto the pane module (#kanban-live-config) — and
      // React Query dedupes the rapid tool_start/receipt/result calls into ~one fetch.
      if (workspaceId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.modules.list(workspaceId) });
      }
      // Objects + health stay debounced — heavier/noisier and not pane-critical.
      if (activeConversation?.id) {
        if (toolInvalidationTimerRef.current) {
          clearTimeout(toolInvalidationTimerRef.current);
        }
        toolInvalidationTimerRef.current = setTimeout(() => {
          invalidateConversationObjects(activeConversation.id);
          // Refresh health profile and stats
          queryClient.invalidateQueries({ queryKey: ['health-profile'] });
          toolInvalidationTimerRef.current = null;
        }, 500); // 500ms debounce — quick refresh after tool execution
      }
      if (voiceModeRef.current?.isActive) {
        voiceModeRef.current.signalProcessing(true);
      }
    },
    onObjectCreated: markCreated,
    onUiActions: (actions) => {
      if (Array.isArray(actions) && actions.length > 0) {
        applyUiActions(actions);
      }
    },
    onStreamEnd: () => {
      // Finalize streaming TTS turn (flush remaining text, mark done)
      try {
        const vm = voiceModeRef.current;
        if (vm?.isActive) {
          vm.finishSpeaking();
        }
      } catch (err) {
        console.error("[ChatBox] finishSpeaking error:", err);
      }
      // Always refresh data when streaming ends (success, error, or abort)
      // This catches cases where SSE dies mid-stream ("Failed to load resource")
      const conversationId = activeConversation?.id;
      const conversationName = activeConversation?.name;
      if (conversationId) {
        // Small delay to let any final writes settle
        setTimeout(() => {
          invalidateConversationStructure(conversationId);
          invalidateConversationObjects(conversationId);
          scheduleAutoTitleFollowupRefresh(conversationId, conversationName);
          // Notify Gmail panel to refresh (drafts, inbox, etc.)
          window.dispatchEvent(new Event('emailDataChanged'));
          // Refresh health data
          queryClient.invalidateQueries({ queryKey: ['health-profile'] });
          if (workspaceId) {
            queryClient.invalidateQueries({ queryKey: queryKeys.objects.byType('stat') });
          }
        }, 500);
        // Follow-up passes for slow server-side post-processing (e.g. place
        // auto-enrich runs in setImmediate after create — Google Places
        // fetch + R2 photo upload can take 5s in the fast path but has been
        // observed at 150s+ when R2 is slow). Without this, agent-created
        // places land in the cache before their photos are written, and the
        // user has to manually refresh. Timers are tracked so navigating
        // away cancels them. #2458
        for (const delayMs of [4000, 10000, 30000, 60000]) {
          const timer = setTimeout(() => {
            invalidateConversationObjects(conversationId);
          }, delayMs);
          postStreamEnrichmentTimersRef.current.push(timer);
        }
      }
      // x-agent posts /internal/usage fire-and-forget on stream end, so the
      // refetch must wait for the write to settle — same 500ms delay the
      // conversation invalidations above use for the same reason.
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['workspace', 'quota'] });
      }, 500);
    },
  };
}
